/**
 * Bundle Checkout - Recharge Integration
 * =======================================
 * Handles conversion of bundleBasket selections to cart items via Recharge API.
 * Used when user clicks "Continue" on subscription step 1.
 *
 * Flow:
 * 1. Collect items from window.bundleBasket
 * 2. Build selections array for Recharge
 * 3. Call recharge.bundle.getDynamicBundleItems()
 * 4. Add converted items to Shopify cart
 * 5. Redirect to step 2
 *
 * Terminology:
 * - window.bundleBasket: in-memory selection (step1). Not necessarily present in Shopify cart yet.
 * - _rc_bundle / _rb_id: line item properties used to group Recharge bundle lines in the Shopify cart.
 */

(() => {
  const BUNDLE_CHECKOUT_EVENT = 'bundle:checkout';

  /**
   * BundleCheckout - Main class for bundle conversion and submission
   */
  class BundleCheckout {
    constructor() {
      this.isProcessing = false;
      this.sellingPlanIndex = 0; // Default to first selling plan (weekly)
    }

    /**
     * Get current bundle basket items
     * @returns {Array} Array of bundle items with metadata
     */
    getBundleItems() {
      if (!window.bundleBasket) return [];

      return Object.entries(window.bundleBasket)
        .filter(([_, item]) => item && item.quantity > 0)
        .map(([variantId, item]) => ({
          variantId,
          ...item
        }));
    }

    /**
     * Get total count of items in bundle
     * @returns {number} Total quantity
     */
    getTotalCount() {
      const items = this.getBundleItems();
      return items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    }

    /**
     * Get count of primary items only (isPrimary === true).
     * Only primary items count toward the minimum-dishes requirement.
     * @returns {number} Primary quantity
     */
    getPrimaryCount() {
      const items = this.getBundleItems();
      return items
        .filter((item) => item.isPrimary)
        .reduce((sum, item) => sum + (item.quantity || 0), 0);
    }

    /**
     * Build selections array for Recharge API
     * @param {Array} items - Bundle items from bundleBasket
     * @returns {Array} Selections for Recharge
     */
    buildSelections(items) {
      if (!window.BundleConfig) {
        console.error('BundleConfig not found');
        return [];
      }

      return items.map((item) => {
        // Get selling plan ID for this item (use first available)
        const sellingPlanId = item.sellingPlanIds?.[this.sellingPlanIndex] || null;

        return {
          collectionId: item.collectionId ?? window.BundleConfig.collectionId,
          externalProductId: item.productId,
          externalVariantId: item.variantId,
          quantity: item.quantity,
          sellingPlan: sellingPlanId
        };
      });
    }

    /**
     * Remove existing bundle items from cart before adding new ones
     * @returns {Promise} Cart update response
     */
    async removeExistingBundleFromCart() {
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        // Find bundle items by Recharge group property (server-side cart lines).
        const bundleItemKeys = cart.items
          .filter((item) => item.properties?._rc_bundle != null)
          .map((item) => item.key);

        if (bundleItemKeys.length === 0) return;

        // Build updates object to set quantities to 0
        const updates = {};
        bundleItemKeys.forEach((key) => {
          updates[key] = 0;
        });

        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
      } catch (error) {
        console.error('Error removing existing bundle:', error);
      }
    }

    /**
     * Main conversion function - converts bundleBasket to cart via Recharge
     * @param {string} redirectUrl - URL to redirect after success
     * @returns {Promise<boolean>} Success status
     */
    async convertAndSubmit(redirectUrl = '/pages/subscription-step-2') {
      if (this.isProcessing) return false;

      const items = this.getBundleItems();
      if (items.length === 0) {
        console.warn('No items in bundle basket');
        return false;
      }

      const totalCount = this.getTotalCount();
      const bar = document.querySelector('.bottom-purchase-bar');
      if (bar?.dataset?.mode !== 'subscription_step_2') {
        const primaryCount = this.getPrimaryCount();
        const minRequired = window.BundleConfig.minMainDishes;
        if (primaryCount < minRequired) {
          console.warn(`Minimum ${minRequired} primary dishes required, got ${primaryCount}`);
          return false;
        }

        const bundleTotal = items.reduce((sum, item) =>
          sum + ((item.price || 0) + (item.surcharges || 0)) * (item.quantity || 0), 0);
        const minAmount = window.BundleConfig.minOrderAmountCents;
        if (bundleTotal < minAmount) {
          console.warn(`Minimum subscription bundle amount ${minAmount} required, got ${bundleTotal}`);
          return false;
        }
      }

      this.isProcessing = true;
      this.emitEvent('start', { totalCount });

      try {
        // Check if Recharge SDK is available
        if (typeof recharge === 'undefined' || !recharge.bundle) {
          throw new Error('Recharge SDK not loaded');
        }

        // Build selections for Recharge
        const selections = this.buildSelections(items);
        const dynamicProduct = window.BundleConfig.dynamicBundleProduct;

        // Build bundle object for Recharge API
        const bundle = {
          externalProductId: dynamicProduct.id,
          externalVariantId: dynamicProduct.variants[0].id,
          selections
        };

        // Call Recharge API to get bundle items
        const bundleItems = await recharge.bundle.getDynamicBundleItems(bundle, dynamicProduct.handle);

        if (!bundleItems || bundleItems.length === 0) {
          throw new Error('Recharge returned no bundle items');
        }

        // Mark first and last items for identification
        bundleItems[0].properties = {
          ...bundleItems[0].properties,
          _bundle_order_flag: 'first'
        };
        bundleItems[bundleItems.length - 1].properties = {
          ...bundleItems[bundleItems.length - 1].properties,
          _bundle_order_flag: 'last'
        };

        // Remove existing bundle items first
        await this.removeExistingBundleFromCart();

        // NOTE: Surcharges are handled by Recharge internally, no need to add separate items
        const allItems = [...bundleItems];

        // Add converted bundle lines to Shopify cart.
        // We also store bundleBasket in cart attributes for debugging / back-office context.
        const addResponse = await fetch(`${routes.cart_add_url}.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest', // This is needed as currently there is a bug in Shopify that assumes this header
          },
          body: JSON.stringify({
            items: allItems,
            attributes: {
              /*number_of_meals: totalCount,*/
              bundleBasket: JSON.stringify(window.bundleBasket)
            }
          })
        });

        if (!addResponse.ok) {
          const errorData = await addResponse.json();
          throw new Error(errorData.description || 'Failed to add items to cart');
        }

        // Clear local bundleBasket after successful submission.
        // From now on the "truth" is the Shopify cart bundle lines created by Recharge.
        window.bundleBasket = {};
        if (window.SmartBuyStore) {
          window.SmartBuyStore.ensureBundleState();
        }

        this.emitEvent('success', { totalCount, itemsAdded: allItems.length });

        // Redirect is done by caller (bottom-purchase-bar.js) after clearing loading state.
        return true;
      } catch (error) {
        console.error('Bundle checkout error:', error);
        this.emitEvent('error', { message: error.message });
        return false;
      } finally {
        this.isProcessing = false;
      }
    }

    /**
     * Emit custom event for UI updates
     * @param {string} type - Event type (start, success, error)
     * @param {Object} detail - Event details
     */
    emitEvent(type, detail = {}) {
      window.dispatchEvent(
        new CustomEvent(BUNDLE_CHECKOUT_EVENT, {
          detail: { type, ...detail }
        })
      );
    }
  }

  // Expose globally
  window.BundleCheckout = new BundleCheckout();

  // Export event name for listeners
  window.BUNDLE_CHECKOUT_EVENT = BUNDLE_CHECKOUT_EVENT;
})();
